import { X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { MultiSelect } from "@/components/MultiSelect";
import { EMPTY_FILTERS, SOURCE_SHORT, presetRange } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const PRESETS = [
  ["all", "All time"], ["today", "Today"], ["7d", "Last 7 days"], ["30d", "Last 30 days"],
  ["this_month", "This month"], ["last_month", "Last month"], ["custom", "Custom dates"],
];

export const FilterBar = ({ sentiment, onSentiment, onClear }) => {
  const { meta, filters, setFilters } = useApp();
  if (!meta) return null;
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const outlets = meta.outlets.filter((o) => o.active !== false && (!filters.brand.length || filters.brand.includes(o.brand))).map((o) => o.name);
  const setPreset = (p) => {
    const [from, to] = p === "custom" ? [filters.date_from, filters.date_to] : presetRange(p);
    setFilters((f) => ({ ...f, preset: p, date_from: from, date_to: to }));
  };
  const active = Object.entries(filters).some(([k, v]) => (Array.isArray(v) ? v.length : k !== "preset" && k !== "q" && v));

  return (
    <div data-testid="filter-bar" className="mb-6 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3">
      <MultiSelect label="Brand" options={meta.brands} value={filters.brand} onChange={set("brand")} testid="filter-brand" />
      <MultiSelect label="Outlet" options={[...new Set(outlets)]} value={filters.outlet} onChange={set("outlet")} testid="filter-outlet" />
      <MultiSelect label="Source" options={Object.keys(meta.source_types)} value={filters.source_type} onChange={set("source_type")} testid="filter-source" renderLabel={(s) => `${s} · ${SOURCE_SHORT[s]}`} />
      <MultiSelect label="Platform" options={meta.platforms} value={filters.platform} onChange={set("platform")} testid="filter-platform" />
      <MultiSelect label="Category" options={meta.categories} value={filters.category} onChange={set("category")} testid="filter-category" />
      <MultiSelect label="Team" options={["FOH", "BOH"]} value={filters.department || []} onChange={set("department")} testid="filter-department" renderLabel={(d) => (d === "FOH" ? "FOH · Service" : "BOH · Kitchen")} />
      <MultiSelect label="Severity" options={meta.severities} value={filters.severity} onChange={set("severity")} testid="filter-severity" />
      <MultiSelect label="Status" options={meta.statuses} value={filters.status} onChange={set("status")} testid="filter-status" />
      <MultiSelect label="Sentiment" options={meta.sentiments} value={sentiment || filters.sentiment} onChange={onSentiment || set("sentiment")} testid="filter-sentiment" />
      <Select value={filters.preset} onValueChange={setPreset}>
        <SelectTrigger data-testid="filter-date-preset" className="h-10 w-[150px] rounded-xl"><SelectValue /></SelectTrigger>
        <SelectContent>
          {PRESETS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`date-preset-${v}`}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
      {filters.preset === "custom" && (
        <div className="flex items-center gap-1">
          <Input type="date" data-testid="filter-date-from" className="h-10 w-[150px] rounded-xl" value={filters.date_from} onChange={(e) => set("date_from")(e.target.value)} />
          <span className="text-slate-400">to</span>
          <Input type="date" data-testid="filter-date-to" className="h-10 w-[150px] rounded-xl" value={filters.date_to} onChange={(e) => set("date_to")(e.target.value)} />
        </div>
      )}
      {(active || onClear) && (
        <button data-testid="filter-clear-all" onClick={() => { setFilters({ ...EMPTY_FILTERS, q: filters.q }); onClear?.(); }} className="ml-auto flex h-10 items-center gap-1 rounded-xl px-3 text-sm font-medium text-slate-500 hover:bg-slate-100">
          <X className="h-4 w-4" /> Clear filters
        </button>
      )}
    </div>
  );
};
